/**
 * Partial-stock availability notice — PDP live updates & split-line logic.
 *
 * Custom element <m-partial-stock> wraps the [data-availability-notice] shell
 * rendered by product-availability-notice.liquid on the PDP.
 *
 * It reads per-variant inventory from a <script type="application/json"
 * data-variant-inventory> tag emitted by product-form.liquid, then:
 *   • Listens to variant:changed and qty input events
 *   • Shows / hides the notice and updates its numbers live
 *   • Handles the "Order available now, pre-order the rest" split-line flow
 *   • Pre-fills the request-delivery modal message with backorder context
 *
 * Cart split-line (data-cart-split-line buttons) is handled separately at
 * the bottom of this file via a delegated document listener. It also
 * intercepts the cart / drawer checkout button to auto-split any remaining
 * partial-stock lines before navigating to /checkout.
 */

(function () {
  'use strict';

  const DEBUG = true; /* set to false once verified */
  const log = (...a) => DEBUG && console.log('[partial-stock]', ...a);
  log('script loaded');

  /* =====================================================================
   * PDP: live partial-stock notice on the product page
   * ===================================================================== */
  log('m-partial-stock elements in DOM right now:', document.querySelectorAll('m-partial-stock').length);

  if (!customElements.get('m-partial-stock')) {
    customElements.define(
      'm-partial-stock',
      class MPartialStock extends HTMLElement {
        connectedCallback() {
          log('connectedCallback fired');

          const inventoryScript = document.querySelector('[data-variant-inventory]');
          if (!inventoryScript) {
            log('connectedCallback: ABORT — no [data-variant-inventory] in DOM');
            return;
          }

          try {
            this._inventory = JSON.parse(inventoryScript.textContent);
            log('connectedCallback: inventory parsed —', Object.keys(this._inventory).length, 'variant(s)');
          } catch (err) {
            log('connectedCallback: ABORT — JSON parse error', err);
            return;
          }

          /* _notice is optional — on PDP only the RDT button is used;
             the full notice is only rendered in cart/drawer contexts. */
          this._notice = this.querySelector('[data-availability-notice]') || null;

          /* _rdtBtn, _deliveryText and _pickupEl may be outside this element,
             so all are queried document-wide and looked up lazily in _update(). */
          this._rdtBtn = null;
          this._deliveryText = null;
          this._pickupEl = null;

          this._variantId = String(document.querySelector('[name="id"]')?.value || '');
          this._qty = 1;

          log('connectedCallback: setup OK — variantId=', this._variantId, '— calling _update()');
          this._bindEvents();
          this._update();
        }

        _bindEvents() {
          document.addEventListener('variant:changed', (e) => {
            const variant = e.detail?.variant;
            if (variant) {
              this._variantId = String(variant.id);
              this._update();
            }
          });

          const onQtyChange = (e) => {
            const input = e.target;
            if (!input.closest('product-form, [data-section-id]')) return;
            if (!input.matches('[name="quantity"], .m-quantity__input')) return;
            const val = parseInt(input.value, 10);
            if (!isNaN(val) && val > 0) {
              this._qty = val;
              this._update();
            }
          };
          document.addEventListener('change', onQtyChange);
          document.addEventListener('input', onQtyChange);

          this.addEventListener('click', (e) => {
            const btn = e.target?.closest('[data-split-line]');
            if (btn) {
              e.preventDefault();
              this._handleSplitLine(btn);
            }
          });

          this.addEventListener('click', (e) => {
            if (e.target?.closest('[data-open-request-delivery]')) {
              this._prefillBackorderMessage();
            }
          });
        }

        _getVariantData() {
          if (!this._variantId || !this._inventory) return null;
          return this._inventory[this._variantId] ?? null;
        }

        _update() {
          /* Lazy lookups — all may be outside this element */
          if (!this._rdtBtn) {
            this._rdtBtn = document.querySelector('[data-partial-stock-rdt]');
            log('_rdtBtn lookup →', this._rdtBtn);
          }
          if (!this._deliveryText) {
            this._deliveryText = document.querySelector('[data-partial-stock-delivery]');
            log('_deliveryText lookup →', this._deliveryText);
          }
          if (!this._pickupEl) {
            this._pickupEl = document.querySelector('pickup-availability[data-partial-stock-pickup]');
            log('_pickupEl lookup →', this._pickupEl);
          }

          const vd = this._getVariantData();
          log('_update: variantId=', this._variantId, 'qty=', this._qty, 'vd=', vd);
          if (!vd || !vd.management) {
            log('_update: HIDE — no vd or unmanaged inventory');
            this._notice?.classList.add('m:hidden');
            if (this._rdtBtn) this._rdtBtn.hidden = true;
            this._restoreDeliveryText();
            if (this._pickupEl) this._pickupEl.hidden = false;
            return;
          }

          const stockQty = vd.quantity;
          const requested = this._qty || 1;
          const canBackorder = vd.policy === 'continue';
          log('_update: stockQty=', stockQty, 'requested=', requested, 'canBackorder=', canBackorder);

          if (stockQty > 0 && requested > stockQty && canBackorder) {
            const availableNow = stockQty;

            if (this._notice) {
              const splitBtn = this._notice.querySelector('[data-split-line]');
              if (splitBtn) {
                splitBtn.dataset.available = availableNow;
                splitBtn.dataset.variantId = this._variantId;
              }
              this._notice.classList.remove('m:hidden');
            }

            if (this._rdtBtn) this._rdtBtn.hidden = false;
            if (this._deliveryText) {
              this._deliveryText.textContent = this._deliveryText.dataset.partial || this._deliveryText.textContent;
            }
            if (this._pickupEl) this._pickupEl.hidden = true;
          } else {
            this._notice?.classList.add('m:hidden');
            if (this._rdtBtn) this._rdtBtn.hidden = true;
            this._restoreDeliveryText();
            if (this._pickupEl) this._pickupEl.hidden = false;
          }
        }

        _restoreDeliveryText() {
          if (this._deliveryText && this._deliveryText.dataset.normal) {
            this._deliveryText.textContent = this._deliveryText.dataset.normal;
          }
        }

        _setText(el, template, count) {
          if (!el) return;
          if (template) {
            el.textContent = template.replace('{{ count }}', count);
          } else {
            const countEl = el.querySelector('[data-count]');
            if (countEl) countEl.textContent = count;
          }
        }

        _prefillBackorderMessage() {
          const msgArea = document.querySelector('[data-rdt-message-default]');
          if (!msgArea) return;

          const available = parseInt(this._notice?.dataset.availableNow || '0', 10);
          const backorder = parseInt(this._notice?.dataset.backorderQty || '0', 10);
          if (available <= 0 || backorder <= 0) return;

          const tpl = window.__partialStockStrings?.backorder_message;
          if (!tpl) return;

          const msg = tpl
            .replaceAll('{{ available }}', available)
            .replaceAll('{{ backorder }}', backorder);

          msgArea.value = msg;
          msgArea.dataset.rdtMessageDefault = msg;
        }

        async _handleSplitLine(btn) {
          const variantId = parseInt(btn.dataset.variantId, 10);
          const availableNow = parseInt(btn.dataset.available, 10);
          const backorderQty = parseInt(btn.dataset.backorder, 10);

          if (!variantId || isNaN(availableNow) || isNaN(backorderQty)) return;
          btn.disabled = true;

          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [
                  { id: variantId, quantity: availableNow },
                  {
                    id: variantId,
                    quantity: backorderQty,
                    properties: { _Backorder: 'true', _Expected_delivery: 'on request' },
                  },
                ],
              }),
            });

            if (res.ok) {
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.openCartDrawer) {
                window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
              }
            } else {
              const err = await res.json().catch(() => ({}));
              if (window.MinimogTheme?.Notification) {
                MinimogTheme.Notification.show({
                  target: document.body,
                  method: 'appendChild',
                  type: 'error',
                  message: err.description || 'Something went wrong. Please try again.',
                  delay: 3000,
                  sticky: false,
                });
              }
            }
          } catch {
            /* Silent fail */
          } finally {
            btn.disabled = false;
          }
        }
      }
    );
  }

  /* =====================================================================
   * Cart / drawer: split-line button + checkout interceptor
   * ===================================================================== */
  if (window.__partialStockCartSplitBound) {
    log('cart listeners already bound — skipping');
    return;
  }
  window.__partialStockCartSplitBound = true;
  log('binding cart listeners');

  /**
   * Reduce a cart line quantity to the immediately available stock amount.
   * The backordered portion is simply removed from the cart (not converted to
   * a backorder line). Uses Shopify's stable line item KEY so index shifts
   * caused by concurrent cart updates cannot address the wrong line.
   */
  async function splitCartLine(lineKey, availableNow) {
    log('reduceCartLine', { lineKey, availableNow });

    const changeRes = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: availableNow }),
    });
    if (!changeRes.ok) {
      const text = await changeRes.text().catch(() => '');
      throw new Error('cart/change.js failed: ' + changeRes.status + ' ' + text);
    }
  }

  /**
   * Collect all currently visible partial-stock notices from the cart/drawer.
   */
  function collectPartialLines() {
    const notices = [...document.querySelectorAll('[data-availability-notice]')];
    log('collectPartialLines found', notices.length, 'notice(s)');

    return notices
      .filter((notice) => !notice.classList.contains('m:hidden') && notice.offsetParent !== null)
      .map((notice) => {
        const splitBtn = notice.querySelector('[data-cart-split-line]');
        if (!splitBtn) {
          log('  skip: missing splitBtn');
          return null;
        }

        const lineKey = splitBtn.dataset.lineKey;
        const variantId = parseInt(splitBtn.dataset.variantId, 10);
        const availableNow = parseInt(splitBtn.dataset.available, 10);

        if (!lineKey || !variantId || isNaN(availableNow)) {
          log('  skip: invalid attrs', { lineKey, variantId, availableNow });
          return null;
        }
        return { lineKey, variantId, availableNow };
      })
      .filter(Boolean);
  }

  /* -------- Manual split button (capture phase, highest priority) -------- */
  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target?.closest('[data-cart-split-line]');
      if (!btn) return;

      log('split button clicked', btn);
      e.preventDefault();
      e.stopPropagation();

      if (btn.disabled) return;
      btn.disabled = true;

      const lineKey = btn.dataset.lineKey;
      const variantId = parseInt(btn.dataset.variantId, 10);
      const availableNow = parseInt(btn.dataset.available, 10);

      if (!lineKey || !variantId || isNaN(availableNow)) {
        log('abort: invalid attrs', { lineKey, variantId, availableNow });
        btn.disabled = false;
        return;
      }

      try {
        await splitCartLine(lineKey, availableNow);
        log('split complete, refreshing cart');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (err) {
        log('split failed', err);
        btn.disabled = false;
      }
    },
    true /* capture: run before any other click listeners / form submission */
  );

  /* -------- Checkout interceptor ----------
   * When a customer proceeds to checkout with one or more partial-stock
   * lines (quantity ordered > quantity in stock), we tag the cart with a
   * Shopify note_attribute so the fulfilment team / weclapp integration can
   * identify these orders for manual review.
   *
   * The cart quantity is NOT reduced automatically; that is only done when
   * the customer explicitly clicks "Menge anpassen".
   * ------------------------------------------------------------------ */

  function tagCartPartialStock(partialLines) {
    /* Build a human-readable attribute value listing affected lines. */
    const detail = partialLines
      .map(({ variantId, availableNow }) => `Variante ${variantId}: ${availableNow} verfügbar`)
      .join('; ');

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { Teillieferung: 'Ja — ' + detail },
      }),
    }).then(() => {
      log('cart tagged with Teillieferung:', detail);
    }).catch((err) => {
      log('cart tagging failed (non-blocking):', err);
    });
  }

  /* Intercept checkout in CAPTURE phase — tag cart, then allow checkout. */
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target?.closest('button[name="checkout"], input[name="checkout"]');
      if (!btn || btn.disabled) return;

      const partialLines = collectPartialLines();
      if (partialLines.length === 0) return; /* nothing partial — proceed normally */

      log('checkout with partial stock — tagging cart, lines:', partialLines.length);
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      btn.disabled = true;
      tagCartPartialStock(partialLines).finally(() => {
        window.location.href = '/checkout';
      });
    },
    true /* capture */
  );

  /* Submit interception as a safety net for programmatic form submissions. */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const checkoutBtn = form.querySelector('button[name="checkout"]');
      if (!checkoutBtn) return;

      const partialLines = collectPartialLines();
      if (partialLines.length === 0) return;

      log('cart form submit with partial stock — tagging cart');
      e.preventDefault();
      e.stopPropagation();

      tagCartPartialStock(partialLines).finally(() => {
        window.location.href = '/checkout';
      });
    },
    true
  );

})();
